import { Box, Text } from "@wix/design-system";
import classNames from "classnames";
import lodash from "lodash";
import React from "react";
import { DoubleLoader, MediaItemErrorMsg } from "./../../ui-components/index.js";
import { getContainerClasses } from "../../_shared/figure/get-container-classes.js";
import { VIDEO_ROOT } from "../data-hooks.js";
import styles from "../statics/styles/default-video-styles-scss.js";
import { Video } from "../viewer/video.js";
const isUploading = (videoData) => videoData?.video?.src?.url?.startsWith?.("blob:") ?? false;
function createFakeProgressStepper(onProgress, approximateExpectedDuration = 5e3) {
  let lastTimeoutId;
  let progress = 0;
  const startDate = Date.now();
  const getTimeoutDelay = () => {
    const linearStep = approximateExpectedDuration / 100;
    if (progress <= 25) {
      return linearStep / 4;
    } else if (progress <= 50) {
      return linearStep / 3;
    } else if (progress <= 90) {
      return linearStep / 1.5;
    } else {
      const timeRemaining = approximateExpectedDuration - (Date.now() - startDate);
      return timeRemaining / (100 - progress);
    }
  };
  const run = () => {
    progress += 1;
    if (progress >= 100) {
      return;
    }
    lastTimeoutId = setTimeout(run, getTimeoutDelay());
    onProgress(progress);
  };
  run();
  return () => {
    clearTimeout(lastTimeoutId);
  };
}
class VideoComponent extends React.Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.startFakeLoader = () => {
      if (this.resetFakeLoader) {
        this.resetFakeLoader();
      }
      const isCustomVideo = "video" in this.props.componentData && !!this.props.componentData.video?.src?.id;
      this.resetFakeLoader = createFakeProgressStepper(
        (percent) => this.setState({ fakeLoadingPercent: percent }),
        isCustomVideo ? 5e4 : 8500
      );
    };
    this.handleReady = (player) => {
      if (!this.state.isLoaded) {
        this.setState({ isLoaded: true });
        if (!isUploading(this.props.componentData)) {
          this.resetFakeLoader && this.resetFakeLoader();
        }
      }
      this.handleDuration(player);
    };
    this.handleDuration = (player) => {
      setTimeout(() => {
        const duration = player.current?.getDuration();
        if (duration) {
          this.saveDurationToData(duration);
        } else {
          this.setState({ shouldRenderDurationFix: true });
        }
      }, 3e3);
    };
    this.renderPendingOverlay = ({ title, subtitle }) => {
      return /* @__PURE__ */ React.createElement("div", { className: styles.video_pending_overlay }, /* @__PURE__ */ React.createElement("span", { className: styles.video_pending_overlay_loader }, /* @__PURE__ */ React.createElement(DoubleLoader, null)), /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "0", align: "center" }, /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold", light: true }, title), /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "thin", light: true }, subtitle)));
    };
    this.saveDurationToData = (duration) => {
      const { componentData, updateAttributes } = this.props;
      const video = "video" in componentData ? componentData.video : {};
      updateAttributes({ ...componentData, video: { ...video || {}, duration } });
    };
    this.handleUpdateProcessing = (processing) => {
      this.setState({ processing });
    };
    this.renderPlayer = () => {
      const { componentData, config, nodeId } = this.props;
      return /* @__PURE__ */ React.createElement(
        Video,
        {
          componentData,
          config,
          onReady: this.handleReady,
          isLoaded: this.state.isLoaded,
          key: this.state.iosFixKey,
          nodeId,
          isLight: false,
          disabled: true,
          onUpdateProcessing: this.handleUpdateProcessing
        }
      );
    };
    this.renderPlayerDurationFix = () => {
      const { componentData, config, nodeId } = this.props;
      const play = (player) => {
        const internalPlayer = player.current?.getInternalPlayer();
        if (internalPlayer && "play" in internalPlayer && lodash.isFunction(internalPlayer.play)) {
          internalPlayer.play();
          return true;
        }
        return false;
      };
      const onReady = (player) => {
        const hasPlayed = play(player);
        if (hasPlayed) {
          return;
        }
        this.durationFixTimeoutId = setTimeout(() => {
          play(player);
        }, 1e4);
      };
      return /* @__PURE__ */ React.createElement("div", { className: styles.hide_duration }, /* @__PURE__ */ React.createElement(
        Video,
        {
          componentData,
          config,
          onReady,
          isLight: false,
          onDuration: (duration) => {
            this.setState({ shouldRenderDurationFix: false });
            this.saveDurationToData(duration);
            clearTimeout(this.durationFixTimeoutId);
          },
          nodeId,
          muted: true,
          onUpdateProcessing: this.handleUpdateProcessing
        }
      ));
    };
    this.getOverlayProps = () => {
      const { t } = this.props;
      const { isLoaded, processing } = this.state;
      const loading = this.props.isLoading || !isLoaded;
      const uploading = isUploading(this.props.componentData);
      if (processing) {
        return {
          title: t("VideoComponent_Processing_Title"),
          subtitle: t("VideoComponent_Processing_Subtitle")
        };
      }
      if (loading && !this.props.componentData.error) {
        return {
          title: uploading ? t("VideoComponent_Uploading_Title") : t("VideoComponent_Loading_Title"),
          subtitle: uploading ? t("VideoComponent_Uploading_Subtitle", {
            percent: Math.round(this.state.fakeLoadingPercent)
          }) : t("VideoComponent_Loading_Subtitle")
        };
      }
      return null;
    };
    this.state = {
      isLoaded: false,
      iosFixKey: 1,
      processing: false,
      fakeLoadingPercent: 1
    };
  }
  componentDidMount() {
    this.handlePlayerFocus();
    if (isUploading(this.props.componentData)) {
      this.startFakeLoader();
    }
  }
  componentDidUpdate(prevProps) {
    this.handlePlayerFocus();
    const wasUploading = isUploading(prevProps.componentData);
    const isNowUploading = isUploading(this.props.componentData);
    const hasTokenChanged = prevProps.componentData.uploadToken !== this.props.componentData.uploadToken && this.props.componentData.uploadToken !== null;
    if (wasUploading && !isNowUploading || hasTokenChanged) {
      this.resetFakeLoader && this.resetFakeLoader();
    }
    if (!wasUploading && isNowUploading || hasTokenChanged) {
      this.startFakeLoader();
    }
  }
  componentWillUnmount() {
    clearTimeout(this.durationFixTimeoutId);
    this.resetFakeLoader && this.resetFakeLoader();
  }
  handlePlayerFocus() {
    const element = this.containerRef.current?.querySelector("iframe, video");
    if (element) {
      element.tabIndex = -1;
    }
  }
  render() {
    const {
      t,
      componentData: { error }
    } = this.props;
    const { shouldRenderDurationFix } = this.state;
    const overlayProps = this.getOverlayProps();
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: this.containerRef,
        "data-hook": VIDEO_ROOT,
        className: classNames(
          styles.video_container,
          getContainerClasses(this.props.editor, this.props.nodeId, false)
        )
      },
      /* @__PURE__ */ React.createElement("div", { className: styles.video_overlay }),
      this.renderPlayer(),
      shouldRenderDurationFix ? this.renderPlayerDurationFix() : null,
      overlayProps && this.renderPendingOverlay(overlayProps),
      error && /* @__PURE__ */ React.createElement(MediaItemErrorMsg, { error, t })
    );
  }
}
export {
  VideoComponent
};
//# sourceMappingURL=video-component.js.map
